import "../assets/css/CustomDualListbox.css";
import React, { useState, useEffect, useRef, useCallback } from "react";

const CustomDualListBox = ({ options, selected, onChange, highlightedValues = [], disabledValues = [] }) => {
  const highlightScrolledRef = useRef(false);
  const highlightedItemRef = useCallback((node) => {
    if (node && !highlightScrolledRef.current) {
      highlightScrolledRef.current = true;
      setTimeout(() => {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
  }, []);
  const [availableOptions, setAvailableOptions] = useState(
    options.filter((opt) => !selected.includes(opt.value))
  );
  const [selectedOptions, setSelectedOptions] = useState(
    options.filter((opt) => selected.includes(opt.value))
  );

  const [searchAvailable, setSearchAvailable] = useState("");
  const [searchSelected, setSearchSelected] = useState("");

  // State to track selected items
  const [selectedAvailableItems, setSelectedAvailableItems] = useState([]);
  const [selectedSelectedItems, setSelectedSelectedItems] = useState([]);

  const [isFocused, setIsFocused] = useState(false);
  const [isSelectedFocused, setIsSelectedFocused] = useState(false);

  // Sync internal state when selected prop or options change externally
  const selectedKey = JSON.stringify(selected);
  const optionsLength = options.length;
  useEffect(() => {
    setSelectedOptions(options.filter((opt) => selected.includes(opt.value)));
    setAvailableOptions(options.filter((opt) => !selected.includes(opt.value)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, optionsLength]);

  const filteredAvailableOptions = availableOptions
    .filter((opt) =>
      opt.label.toLowerCase().includes(searchAvailable.toLowerCase())
    )
    .sort((a, b) => {
      const searchLower = searchAvailable.toLowerCase();
      return a.label.toLowerCase().startsWith(searchLower) ===
        b.label.toLowerCase().startsWith(searchLower)
        ? a.label.localeCompare(b.label)
        : a.label.toLowerCase().startsWith(searchLower)
        ? -1
        : 1;
    });

  const filteredSelectedOptions = selectedOptions
    .filter((opt) =>
      opt.label.toLowerCase().includes(searchSelected.toLowerCase())
    )
    .sort((a, b) => {
      const searchLower = searchSelected.toLowerCase();
      return a.label.toLowerCase().startsWith(searchLower) ===
        b.label.toLowerCase().startsWith(searchLower)
        ? a.label.localeCompare(b.label)
        : a.label.toLowerCase().startsWith(searchLower)
        ? -1
        : 1;
    });

  // Handle item selection in Available List
  const handleSelectAvailable = (event, value) => {
    if (event.ctrlKey || event.metaKey) {
      // Multi-select with Ctrl
      setSelectedAvailableItems((prev) =>
        prev.includes(value)
          ? prev.filter((v) => v !== value)
          : [...prev, value]
      );
    } else {
      // Single select (Remove all other selections)
      setSelectedAvailableItems([value]);
    }
  };

  // Handle item selection in Selected List (disabled items can't be selected for moving)
  const handleSelectSelected = (event, value) => {
    if (disabledValues.includes(value)) return;
    if (event.ctrlKey || event.metaKey) {
      setSelectedSelectedItems((prev) =>
        prev.includes(value)
          ? prev.filter((v) => v !== value)
          : [...prev, value]
      );
    } else {
      setSelectedSelectedItems([value]);
    }
  };

  // Move selected items to the "selected" list
  const moveToSelected = () => {
    const movingItems = availableOptions.filter((opt) =>
      selectedAvailableItems.includes(opt.value)
    );

    setAvailableOptions(
      availableOptions.filter(
        (opt) => !selectedAvailableItems.includes(opt.value)
      )
    );
    setSelectedOptions([...selectedOptions, ...movingItems]);

    // Update parent state
    onChange([...selected, ...movingItems.map((opt) => opt.value)]);

    setSelectedAvailableItems([]); // Reset selection
    setSearchAvailable(""); // Clear search
  };

  // Move selected items back to "available" list (skips disabled items)
  const moveToAvailable = () => {
    const movableItems = selectedOptions.filter((opt) =>
      selectedSelectedItems.includes(opt.value) && !disabledValues.includes(opt.value)
    );
    if (movableItems.length === 0) return;

    const movableValues = movableItems.map((opt) => opt.value);
    setSelectedOptions(
      selectedOptions.filter(
        (opt) => !movableValues.includes(opt.value)
      )
    );
    setAvailableOptions([...availableOptions, ...movableItems]);

    onChange(selected.filter((val) => !movableValues.includes(val)));

    setSelectedSelectedItems([]);
    setSearchSelected("");
  };

  // Move all available options to selected
  const moveAllToSelected = () => {
    setSelectedOptions([...selectedOptions, ...availableOptions]);
    setAvailableOptions([]);
    onChange([...selected, ...availableOptions.map((opt) => opt.value)]);
    setSelectedAvailableItems([]); // Reset selection
    setSearchAvailable(""); // Clear search
  };

  // Move all selected options back to available (keeps disabled items on the right)
  const moveAllToAvailable = () => {
    const movable = selectedOptions.filter((opt) => !disabledValues.includes(opt.value));
    const kept = selectedOptions.filter((opt) => disabledValues.includes(opt.value));
    setAvailableOptions([...availableOptions, ...movable]);
    setSelectedOptions(kept);
    onChange(kept.map((opt) => opt.value));
    setSelectedSelectedItems([]);
    setSearchSelected("");
  };

  // Handle double-click on Available List (Move to Selected)
  const handleDoubleClickAvailable = (value) => {
    const movingItem = availableOptions.find((opt) => opt.value === value);
    if (movingItem) {
      setAvailableOptions(
        availableOptions.filter((opt) => opt.value !== value)
      );
      setSelectedOptions([...selectedOptions, movingItem]);

      onChange([...selected, movingItem.value]);
      setSearchAvailable(""); // Clear search
    }
  };

  // Handle double-click on Selected List (Move back to Available — blocked for disabled items)
  const handleDoubleClickSelected = (value) => {
    if (disabledValues.includes(value)) return;
    const movingItem = selectedOptions.find((opt) => opt.value === value);
    if (movingItem) {
      setSelectedOptions(selectedOptions.filter((opt) => opt.value !== value));
      setAvailableOptions([...availableOptions, movingItem]);

      onChange(selected.filter((val) => val !== value));
      setSearchSelected(""); // Clear search
    }
  };

  return (
    <>
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css"
      ></link>
      <div className="dual-list-container">
        {/* Available List */}
        <div className="list">
          <input
            type="text"
            placeholder="Search..."
            value={searchAvailable}
            onChange={(e) => setSearchAvailable(e.target.value)}
            className="search-input"
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            style={{
              border: isFocused
                ? "1px solid var(--secondary)"
                : "1px solid #ccc", // Change color on focus
            }}
          />
          <div className="scrollable-list">
            {filteredAvailableOptions.length > 0 ? (
              filteredAvailableOptions.map((option) => (
                <div
                  key={option.value}
                  className={`list-item ${
                    selectedAvailableItems.includes(option.value)
                      ? "selected"
                      : ""
                  }`}
                  onClick={(e) => handleSelectAvailable(e, option.value)}
                  onDoubleClick={() => handleDoubleClickAvailable(option.value)}
                >
                  {option.label}
                </div>
              ))
            ) : (
              <div className="empty-message"></div>
            )}
          </div>
        </div>

        {/* Button Group */}
        <div className="button-group">
          <button
            onClick={moveAllToSelected}
            disabled={availableOptions.length === 0}
          >
            <i className="fa-solid fa-angles-down"></i>
          </button>
          <button
            onClick={moveToSelected}
            disabled={selectedAvailableItems.length === 0}
          >
            <i className="fa fa-chevron-right" aria-hidden="true"></i>
          </button>
          <button
            onClick={moveToAvailable}
            disabled={selectedSelectedItems.length === 0}
          >
            <i className="fa fa-chevron-left" aria-hidden="true"></i>
          </button>
          <button
            onClick={moveAllToAvailable}
            disabled={selectedOptions.length === 0}
          >
            <i className="fa-solid fa-angles-up"></i>
          </button>
        </div>

        {/* Selected List */}
        <div className="list">
          <input
            type="text"
            placeholder="Search..."
            value={searchSelected}
            onChange={(e) => setSearchSelected(e.target.value)}
            className="search-input"
            onFocus={() => setIsSelectedFocused(true)}
            onBlur={() => setIsSelectedFocused(false)}
            style={{
              border: isSelectedFocused
                ? "1px solid var(--cn)"
                : "1px solid #ccc", // Change color on focus for selected list
            }}
          />
          <div className="scrollable-list">
            {filteredSelectedOptions.length > 0 ? (
              filteredSelectedOptions.map((option) => {
                const isHighlighted = highlightedValues.includes(option.value);
                const isDisabled = disabledValues.includes(option.value);
                return (
                  <div
                    key={option.value}
                    ref={isHighlighted ? highlightedItemRef : undefined}
                    className={`list-item ${
                      selectedSelectedItems.includes(option.value)
                        ? "selected"
                        : ""
                    } ${
                      isHighlighted ? "list-item--highlighted" : ""
                    } ${
                      isDisabled ? "list-item--disabled" : ""
                    }`}
                    onClick={(e) => handleSelectSelected(e, option.value)}
                    onDoubleClick={() => handleDoubleClickSelected(option.value)}
                  >
                    {option.label}
                  </div>
                );
              })
            ) : (
              <div className="empty-message"></div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomDualListBox;
